/**
 * Template di pre-compilazione "Premessa" basati sul tipo di indagine.
 * Adattano il testo al genere dell'indagato (M/F) e inseriscono il nome.
 *
 * Casi coperti:
 *   - infedelta_coniugale
 *   - falsa_malattia       → template "Infedeltà Lavorativa"
 *   - abuso_104            → template "Infedeltà Lavorativa"
 *   - concorrenza_sleale
 *   - altro / unmapped     → nessun template (testo vuoto)
 */

/**
 * Ritorna l'appellativo (Sig./Sig.ra) in base al genere.
 */
const appellative = (gender) => (gender === 'F' ? 'Sig.ra' : 'Sig.');

/**
 * Ritorna il pronome riflessivo di terza persona singolare.
 *   F → "la stessa"
 *   M / default → "lo stesso"
 */
const pronoun = (gender) => (gender === 'F' ? 'la stessa' : 'lo stesso');

/**
 * Articolo/preposizione dativo per "del/della":
 *   F → "della"
 *   M / default → "del"
 */
const art = (gender) => (gender === 'F' ? 'della' : 'del');

/**
 * Compone il nome completo dalla struttura investigated presente in caseData.
 * Accetta sia un oggetto { first_name, last_name } sia una stringa singola.
 */
export function getInvestigatedFullName(investigated) {
    if (!investigated) return '';
    if (typeof investigated === 'string') return investigated.trim();
    const parts = [];
    if (investigated.first_name) parts.push(investigated.first_name);
    if (investigated.last_name) parts.push(investigated.last_name);
    return parts.join(' ').trim();
}

/**
 * Genera il testo di premessa in base al tipo caso.
 *
 * @param {string} caseType - uno dei valori CASE_TYPES
 * @param {string} investigatedFullName - nome e cognome dell'indagato/a
 * @param {'M'|'F'|undefined} gender - genere dell'indagato
 * @returns {string} testo precompilato oppure stringa vuota se tipo non supportato
 */
export function generatePremiseTemplate(caseType, investigatedFullName, gender) {
    const nameSafe = (investigatedFullName && investigatedFullName.trim()) || 'XXXX';
    const A = appellative(gender);        // "Sig." o "Sig.ra"
    const P = pronoun(gender);             // "lo stesso" o "la stessa"
    const AR = art(gender);                // "del" o "della"

    switch (caseType) {
        case 'infedelta_coniugale':
            return (
`Il Cliente richiede di eseguire indagini aventi l'esclusiva e specifica finalità di far valere o difendere un diritto in sede stragiudiziale e/o giudiziale per la raccolta di elementi probatori, utili a verificare la presunta infedeltà coniugale e le condotte ${AR} ${A} ${nameSafe}.

L'attività investigativa è volta a verificare la condotta ${AR} ${A} ${nameSafe}, in quanto sussistono fondati motivi per ritenere che ${P} non adempia pienamente ai propri doveri genitoriali; viene segnalata la possibile sussistenza di comportamenti non coerenti con il ruolo genitoriale.`
            );

        case 'falsa_malattia':
        case 'abuso_104':
            {
                // "Il Sig." / "La Sig.ra" (con articolo iniziale maiuscolo)
                const APP = gender === 'F' ? 'La Sig.ra' : 'Il Sig.';
                return (
`Il Cliente richiede di eseguire indagini aventi l'esclusiva e specifica finalità di far valere o difendere un diritto in sede stragiudiziale e/o giudiziale per la raccolta di elementi probatori utili a verificare eventuali violazioni degli obblighi di buona fede e correttezza contrattuale da parte del proprio dipendente ${APP} ${nameSafe}.`
                );
            }

        case 'concorrenza_sleale':
            return (
`Il Cliente richiede di eseguire indagini aventi l'esclusiva e specifica finalità di far valere o difendere un diritto in sede stragiudiziale e/o giudiziale per la raccolta di elementi probatori, utili a verificare la concorrenza sleale ${AR} ${A} ${nameSafe}.

L'attività investigativa è volta a verificare la condotta ${AR} ${A} ${nameSafe}, in quanto sussistono fondati motivi per ritenere che ${P}, in violazione del patto di non concorrenza, sia in procinto di passare con Istituto di Credito concorrente.`
            );

        default:
            // 'altro' o qualunque altro valore → nessun template
            return '';
    }
}

/**
 * Verifica se un testo "premessa" corrisponde ad uno dei template generabili.
 * Utile per capire se il campo è stato modificato manualmente dall'utente
 * (e quindi NON va sovrascritto) oppure è ancora un template auto-generato.
 *
 * Metodo: genera tutti i template possibili combinando i tipi caso × i generi
 * con lo stesso nome indagato, e confronta.
 */
export function isAutoGeneratedPremise(text, investigatedFullName) {
    if (!text) return true; // vuoto = ok sovrascrivere
    const types = ['infedelta_coniugale', 'falsa_malattia', 'abuso_104', 'concorrenza_sleale'];
    const genders = [undefined, 'M', 'F'];
    const candidates = new Set();
    for (const t of types) {
        for (const g of genders) {
            candidates.add(generatePremiseTemplate(t, investigatedFullName, g).trim());
        }
    }
    return candidates.has(text.trim());
}
